/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { TagsInterface } from "interfaces";
import { PrescriptionTypeEnum } from "medicalsystem-shared";

/**
 * Interface for template API response containing template records with tags.
 */
interface TemplateResponse {
  results: Array<{
    tags?: string[];
  }>;
}

/**
 * Filters out prescription type enum values from a tag array.
 * Removes LAB_ORDER, IMAGING_ORDER, and MEDICINE_PRESCRIPTION from custom tags.
 * @param tags - Array of tag strings to filter
 * @returns Filtered array excluding prescription type enums
 */
export const filterPrescriptionTypes = (tags: string[]): string[] => {
  return tags.filter(
    (tag) =>
      tag !== PrescriptionTypeEnum.LAB_ORDER &&
      tag !== PrescriptionTypeEnum.IMAGING_ORDER &&
      tag !== PrescriptionTypeEnum.MEDICINE_PRESCRIPTION
  );
};

/**
 * Converts an array of tag strings into TagsInterface objects with unique IDs.
 * @param tags - Array of tag name strings
 * @returns Array of tag objects with generated UUIDs and names
 */
export const createTagObjects = (tags: string[]): TagsInterface[] => {
  return tags.map((tag) => ({
    id: crypto.randomUUID(),
    name: tag,
  }));
};

/**
 * Processes template API response to extract and organize tags.
 * Filters out prescription types, creates tag objects, and matches with existing filter tags.
 * @param response - Template API response containing tag arrays
 * @param existingFilterTags - Optional array of previously selected filter tags
 * @returns Object with availableTags (all unique tags) and selectedTags (matching existing filters)
 */
export const processTemplateTags = (
  response: TemplateResponse,
  existingFilterTags: TagsInterface[] = []
): { availableTags: TagsInterface[]; selectedTags: TagsInterface[] } => {
  const tags: string[] = response.results
    .flatMap((element: { tags?: string[] }) => element.tags || [])
    .filter((tag): tag is string => Boolean(tag));

  const uniqueTags: string[] = [...new Set(tags)];
  const filteredTags = filterPrescriptionTypes(uniqueTags);
  const tagObjects = createTagObjects(filteredTags);

  let selectedTags: TagsInterface[] = [];
  if (existingFilterTags?.length > 0) {
    selectedTags = tagObjects.filter((tagObj) =>
      existingFilterTags.some(
        (filterItem) => filterItem.name.toLowerCase() === tagObj.name.toLowerCase()
      )
    );
  }

  return {
    availableTags: tagObjects,
    selectedTags,
  };
};

/**
 * Handles adding a new custom tag to available and selected tag lists.
 * Validates tag doesn't already exist before adding with generated UUID.
 * @param newTag - New tag name to add
 * @param availableTags - Current array of available tags
 * @param selectedTags - Current array of selected tags
 * @returns Object with updatedAvailableTags and updatedSelectedTags including the new tag if valid
 */
export const handleAddNewTag = (
  newTag: string,
  availableTags: TagsInterface[],
  selectedTags: TagsInterface[]
): { updatedAvailableTags: TagsInterface[]; updatedSelectedTags: TagsInterface[] } => {
  if (!newTag.trim()) {
    return { updatedAvailableTags: availableTags, updatedSelectedTags: selectedTags };
  }

  const tagExists = availableTags.some(
    (tag) => tag.name.toLowerCase() === newTag.trim().toLowerCase()
  );

  if (tagExists) {
    return { updatedAvailableTags: availableTags, updatedSelectedTags: selectedTags };
  }

  const newTagObject = {
    id: crypto.randomUUID(),
    name: newTag.trim(),
  };

  const updatedAvailableTags = [...availableTags, newTagObject];
  const updatedSelectedTags = [...selectedTags, newTagObject];

  return { updatedAvailableTags, updatedSelectedTags };
};

